import { createClient } from '@kairos/supabase/browser'

import { env } from '~/env'
import { logger } from './logger'
import type { AppError, AppErrorCode } from './result'

/**
 * Typed HTTP client for the admin API.
 *
 * - Calls `supabase.auth.getSession()` first to grab a fresh access token
 *   (Supabase JS auto-refreshes if expired). Attaches as `Authorization:
 *   Bearer <token>`.
 * - Sends a per-request `x-request-id` (UUID) so client + Worker + Sentry
 *   can correlate. Echoed back by the Worker.
 * - Maps non-2xx responses into a typed `AppError`. Error envelope shape
 *   follows the plan: `{ success: false, error: { code, message, requestId } }`.
 * - Retries idempotent GETs once on 5xx / network. Mutations never retry
 *   here — that's a TanStack Query mutation decision.
 *
 * The lint rule `no-restricted-globals: fetch` blocks bare `fetch()` outside
 * this file, which is exactly what we want — the entire app routes through
 * this auth + observability boundary.
 */

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  readonly body?: unknown
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>
  readonly signal?: AbortSignal
  readonly retry?: boolean
  /**
   * Idempotency-Key header value. Set by destructive mutations (refund,
   * force-delete, role-assign, etc.) — see `lib/idempotency.ts` for the
   * per-dialog generator. Worker rejects POST/PATCH/DELETE on protected
   * routes without this header.
   */
  readonly idempotencyKey?: string
}

interface ErrorEnvelope {
  readonly success: false
  readonly error: {
    readonly code: AppErrorCode
    readonly message: string
    readonly requestId?: string
    readonly details?: unknown
  }
}

interface SuccessEnvelope<T> {
  readonly success: true
  readonly data: T
  readonly requestId?: string
}

type ApiEnvelope<T> = SuccessEnvelope<T> | ErrorEnvelope

const TIMEOUT_MS = 30_000

/** The bearer header every admin call carries; the assistant's streaming transport needs it too. */
export async function bearerAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken()
  return { Authorization: `Bearer ${token}` }
}

async function getAccessToken(): Promise<string> {
  // The browser Supabase client is primed in main.tsx — this returns the
  // already-authenticated singleton, refreshing the token if expired.
  const supabase = createClient()
  const { data, error } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (error || !token) {
    throw new HttpError({
      code: 'unauthorized',
      message: 'Your admin session has expired. Sign in again.',
      status: 401,
    })
  }
  return token
}

function buildUrl(
  path: string,
  query?: RequestOptions['query'],
): string {
  const base = env.VITE_ADMIN_API_URL.replace(/\/$/, '')
  const url = new URL(path.startsWith('/') ? path : `/${path}`, `${base}/`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

function classify(status: number): AppErrorCode {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 409) return 'conflict'
  if (status === 422) return 'validation'
  if (status === 429) return 'rate_limited'
  if (status >= 500) return 'server_error'
  return 'unknown'
}

export function isRetryableError(error: unknown): boolean {
  if (!isAppErrorLike(error)) return false
  return error.code === 'network' || error.code === 'server_error'
}

function isAppErrorLike(value: unknown): value is AppError {
  if (typeof value !== 'object' || value === null) return false
  const e = value as Partial<AppError>
  return typeof e.code === 'string' && typeof e.message === 'string'
}

class HttpError extends Error implements AppError {
  readonly code: AppErrorCode
  readonly status?: number
  readonly requestId?: string
  readonly details?: unknown

  constructor(args: {
    code: AppErrorCode
    message: string
    status?: number
    requestId?: string
    details?: unknown
  }) {
    super(args.message)
    this.name = 'HttpError'
    this.code = args.code
    if (args.status !== undefined) this.status = args.status
    if (args.requestId !== undefined) this.requestId = args.requestId
    if (args.details !== undefined) this.details = args.details
  }
}

/**
 * Parse the `Retry-After` header per RFC 7231 §7.1.3 — accepts either an
 * integer number of seconds (`120`) or an HTTP-date (`Wed, 21 Oct 2026
 * 07:28:00 GMT`). Returns the wait time in milliseconds, or `undefined`
 * if the header is absent / unparseable.
 *
 * Used by 429 toasts so the SPA can show the exact wait time instead of
 * a generic "try again later".
 */
export function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined
  const seconds = Number(header)
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000
  const date = Date.parse(header)
  if (Number.isFinite(date)) {
    const delta = date - Date.now()
    return delta > 0 ? delta : 0
  }
  return undefined
}

async function doFetch<T>(
  path: string,
  options: RequestOptions,
  attempt: number,
  /**
   * Caller-supplied request id. Set by the retry path so a single logical
   * request keeps one id across all attempts — Sentry traces and Worker
   * logs can then group retries instead of treating each as an unrelated
   * event.
   */
  carriedRequestId?: string,
): Promise<T> {
  const requestId = carriedRequestId ?? crypto.randomUUID()
  const url = buildUrl(path, options.query)
  const method = options.method ?? 'GET'
  const token = await getAccessToken()

  const headers = new Headers({
    'content-type': 'application/json',
    accept: 'application/json',
    'x-request-id': requestId,
  })
  if (token) headers.set('authorization', `Bearer ${token}`)
  if (options.idempotencyKey) headers.set('idempotency-key', options.idempotencyKey)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)
  const signal = options.signal
    ? mergeSignals(options.signal, controller.signal)
    : controller.signal

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : null,
      credentials: 'omit',
      signal,
    })
  } catch (cause: unknown) {
    clearTimeout(timeoutId)
    const isAbort = cause instanceof DOMException && cause.name === 'AbortError'
    const message = isAbort
      ? 'Request timed out'
      : cause instanceof Error
        ? cause.message
        : 'Network failure'
    throw new HttpError({ code: 'network', message, requestId })
  }
  clearTimeout(timeoutId)

  const responseRequestId = response.headers.get('x-request-id') ?? requestId

  if (response.status === 204) {
    return undefined as T
  }

  let parsed: ApiEnvelope<T> | null = null
  try {
    parsed = (await response.json()) as ApiEnvelope<T>
  } catch {
    // Non-JSON response — fall through to status-based error.
  }

  if (!response.ok) {
    const code = classify(response.status)
    const message =
      parsed && !parsed.success ? parsed.error.message : response.statusText
    const errorEnvelope = parsed && !parsed.success ? parsed.error : undefined
    // For 429 responses, surface the parsed Retry-After (ms) in `details`
    // so the toast can show "Try again in N seconds" without re-parsing.
    const retryAfterMs =
      response.status === 429
        ? parseRetryAfter(response.headers.get('retry-after'))
        : undefined
    const httpError = new HttpError({
      code,
      message,
      status: response.status,
      requestId: errorEnvelope?.requestId ?? responseRequestId,
      details:
        retryAfterMs !== undefined
          ? { retryAfterMs, ...(errorEnvelope?.details as object | undefined) }
          : errorEnvelope?.details,
    })

    // Retry idempotent GET on 5xx / network. Cap at 1 retry total.
    if (
      method === 'GET' &&
      options.retry !== false &&
      attempt === 0 &&
      (code === 'server_error' || code === 'network')
    ) {
      logger.warn('http retry', {
        url,
        attempt,
        status: response.status,
        requestId,
      })
      // Thread the same `requestId` through the retry so the trace stays
      // single-id end-to-end (matches the Worker's `x-request-id` echo).
      return doFetch<T>(path, options, attempt + 1, requestId)
    }

    logger.warn('http error', {
      url,
      method,
      status: response.status,
      code,
      requestId: responseRequestId,
    })
    throw httpError
  }

  if (!parsed || parsed.success !== true) {
    throw new HttpError({
      code: 'unknown',
      message: 'Malformed response envelope',
      status: response.status,
      requestId: responseRequestId,
    })
  }

  return parsed.data
}

function mergeSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (a.aborted) return a
  if (b.aborted) return b
  const controller = new AbortController()
  const onAbort = (): void => controller.abort()
  a.addEventListener('abort', onAbort, { once: true })
  b.addEventListener('abort', onAbort, { once: true })
  return controller.signal
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return doFetch<T>(path, options, 0)
}

export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/**
 * An authenticated GET whose answer is a file, not an envelope: the printed
 * billing documents (HTML preview, PDF). Errors come back as the same
 * `HttpError` the JSON path throws, read from the error envelope when the
 * Worker sent one.
 */
export async function requestBlob(path: string, query?: RequestOptions['query']): Promise<Blob> {
  const requestId = crypto.randomUUID()
  const token = await getAccessToken()
  const headers = new Headers({ 'x-request-id': requestId })
  if (token) headers.set('authorization', `Bearer ${token}`)
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)
  let response: Response
  try {
    response = await fetch(buildUrl(path, query), { method: 'GET', headers, credentials: 'omit', signal: controller.signal })
  } catch (cause: unknown) {
    clearTimeout(timeoutId)
    throw new HttpError({ code: 'network', message: cause instanceof Error ? cause.message : 'Network failure', requestId })
  }
  clearTimeout(timeoutId)
  if (!response.ok) {
    let message = response.statusText
    try {
      const parsed = (await response.json()) as ApiEnvelope<unknown>
      if (!parsed.success) message = parsed.error.message
    } catch {
      // Not an envelope: the status text is the message.
    }
    throw new HttpError({ code: classify(response.status), message, status: response.status, requestId })
  }
  return response.blob()
}
